/**
 * `gpipayment` — a real Form.io custom component for GPI card capture.
 *
 * Form authors drag this component into a form in the builder, and it renders in every surface
 * (builder canvas, editor preview, sandbox, EMR and portal submissions). Card data never enters
 * Form.io submission data (`input: false` / `persistent: false`) — it flows through dedicated
 * endpoints + parked columns.
 *
 * It is a plain `@formio/js` component class (NOT Angular Elements) because the card fields are
 * Global Payments hosted iframes: mid-entry the typed digits live only inside those cross-origin
 * iframes, and Form.io redraws (conditional logic / calculated values) would tear down and
 * re-create an Angular custom element on every render — losing in-progress card entry. Overriding
 * `redraw()` lets us leave a connected host node untouched instead.
 *
 * At submission runtime the host app supplies an `options.gpi` bridge (see form-submission.ts)
 * that mounts the Angular `EaseGpiCardCapture` widget into our host div. Without that bridge
 * (builder / preview / sandbox) we render a static, non-interactive placeholder card.
 *
 * Direct `@formio/js` usage is a sanctioned exception to the v2 AI rules (formio-library layer).
 */
import { Components } from '@formio/js';
import { escapeHtml } from '@shared/utils/escape-html';
import { formioFieldComponent } from '../formio-angular-host';

/** Runtime bridge the host app hangs off `options.gpi` to mount/refresh/destroy the Angular widget. */
export interface GpiBridge {
  /** Mount the Angular card-capture widget into `hostEl`; returns an opaque handle. */
  mount(hostEl: HTMLElement): GpiMountHandle;
  /** Refresh the mounted widget's data-driven inputs (readonly / existing card / declined). */
  refresh(handle: GpiMountHandle): void;
  /** Tear down the mounted widget. */
  destroy(handle: GpiMountHandle): void;
}

/** Opaque handle returned by {@link GpiBridge.mount} and passed back to refresh/destroy. */
export type GpiMountHandle = unknown;

/** Form.io component type id for the GPI card-capture component. */
export const GPI_PAYMENT_TYPE = 'gpipayment';

/**
 * Default component key. Kept as `gpiPayment` so the rendered `.formio-component-gpiPayment`
 * CSS class matches historical selectors/styling (and the legacy sentinel key).
 */
export const GPI_PAYMENT_KEY = 'gpiPayment';

/** The `ref` attribute of the host div the Angular widget mounts into. */
const GPI_HOST_REF = 'gpiHost';

/**
 * Whether a Form.io `redraw()` should preserve the already-mounted card-capture host instead of
 * tearing the subtree down and rebuilding it. True only when the runtime bridge is present, a
 * widget is mounted, and its host is still connected to the DOM — in that case we just refresh
 * inputs, so the cross-origin card iframes keep their mid-entry input.
 * A detached host (Form.io replaced the subtree) or a first render falls through to a full
 * mount. Pure so the iframe-preservation constraint is unit-testable without bootstrapping
 * Form.io.
 */
export function shouldPreserveGpiHostOnRedraw(state: {
  hasBridge: boolean;
  hasHandle: boolean;
  hostConnected: boolean;
}): boolean {
  return state.hasBridge && state.hasHandle && state.hostConnected;
}

/** Base schema for a `gpipayment` component. Consumed by `GpiPaymentComponent.schema()`. */
export function gpiPaymentSchema(): Record<string, unknown> {
  return {
    type: GPI_PAYMENT_TYPE,
    key: GPI_PAYMENT_KEY,
    // User-facing label — "GPI" is an internal abstraction, so the UI says "Credit Card Capture".
    label: 'Credit Card Capture',
    // Card data must NOT enter Form.io submission data — it lives in parked columns + endpoints.
    input: false,
    persistent: false,
    tableView: false,
    // The placeholder/widget renders its own heading, so suppress the base component label.
    hideLabel: true,
  };
}

// The base `field` class is plain JS with no shipped types. Direct formio usage is sanctioned;
// we describe just the surface we call so the subclass stays type-checked without `any`.
const FieldComponent = formioFieldComponent<{ gpi?: GpiBridge; gpiPlaceholder?: boolean }>();

/**
 * Optional translate hook so the user-facing copy this class emits (placeholder card + builder
 * settings labels) is localized where the host app has i18n. English fallbacks cover contexts
 * without a hook (Form.io's own builder chrome is not otherwise translated).
 */
let translateFn: ((key: string) => string) | null = null;

function tr(key: string, fallback: string): string {
  return translateFn?.(key) ?? fallback;
}

function placeholderCopy(): { title: string; body: string } {
  return {
    title: tr('forms.gpiCardCapture.placeholderTitle', 'Credit Card Capture'),
    body: tr(
      'forms.gpiCardCapture.placeholderBody',
      'Patients and staff will see the secure card form here when a merchant configuration exists.'
    ),
  };
}

/** Exported for lifecycle unit tests; register it via {@link registerGpiPaymentComponent}. */
export class GpiPaymentComponent extends FieldComponent {
  private gpiHandle: GpiMountHandle | null = null;
  private mountedHostEl: HTMLElement | null = null;

  static schema(...extend: unknown[]): Record<string, unknown> {
    return FieldComponent.schema(gpiPaymentSchema(), ...extend);
  }

  static get builderInfo(): Record<string, unknown> {
    return {
      title: tr('forms.gpiCardCapture.builderTitle', 'Credit Card Capture'),
      group: 'integrated',
      icon: 'credit-card',
      weight: 10,
      schema: GpiPaymentComponent.schema(),
    };
  }

  // Minimal settings dialog: label + key only (the base field's Data/Validation/Conditional
  // tabs are irrelevant for a non-input display component).
  static editForm(): Record<string, unknown> {
    return {
      components: [
        {
          type: 'tabs',
          key: 'tabs',
          components: [
            {
              label: tr('forms.gpiCardCapture.editDisplayTab', 'Display'),
              key: 'display',
              weight: 0,
              components: [
                {
                  type: 'textfield',
                  key: 'label',
                  label: tr('forms.gpiCardCapture.editLabel', 'Label'),
                  input: true,
                },
                {
                  type: 'textfield',
                  key: 'key',
                  label: tr('forms.gpiCardCapture.editKey', 'Property Name (Key)'),
                  input: true,
                },
              ],
            },
          ],
        },
      ],
    };
  }

  render(): string {
    // Runtime submission (EMR/portal): a bridge is supplied → mount the live Angular widget.
    if (this.options?.gpi) {
      return super.render(`<div ref="${GPI_HOST_REF}"></div>`);
    }
    // Authoring contexts (builder canvas, editor preview, sandbox) → static placeholder card.
    // Everywhere else that lacks the bridge — notably the legacy v1 `formio-rize` renderer,
    // which never supported card capture — render nothing rather than a misleading placeholder.
    if (this.builderMode || this.options?.gpiPlaceholder) {
      const { title, body } = placeholderCopy();
      return super.render(
        `<div class="gpi-payment-placeholder tw-rounded-md tw-border tw-border-sage tw-bg-mint tw-p-3">
           <div class="tw-mb-1 tw-font-semibold tw-text-foreground">${escapeHtml(title)}</div>
           <p class="text-small tw-text-muted-foreground tw-mb-0">${escapeHtml(body)}</p>
         </div>`
      );
    }
    return super.render('');
  }

  attach(element: HTMLElement): Promise<void> {
    if (this.options?.gpi) {
      this.loadRefs(element, { [GPI_HOST_REF]: 'single' });
      this.mountGpiHost();
    }
    return super.attach(element);
  }

  // Form.io redraws mid-form for conditional logic / calculated values. Re-rendering the
  // subtree would reload the cross-origin card iframes and lose mid-entry input,
  // so when our host is still connected we skip the DOM teardown and just refresh inputs.
  redraw(): Promise<void> {
    const bridge = this.options?.gpi;
    const preserve = shouldPreserveGpiHostOnRedraw({
      hasBridge: !!bridge,
      hasHandle: this.gpiHandle != null,
      hostConnected: !!this.mountedHostEl?.isConnected,
    });
    if (bridge && preserve) {
      bridge.refresh(this.gpiHandle);
      return Promise.resolve();
    }
    return super.redraw();
  }

  destroy(...args: unknown[]): void {
    if (this.gpiHandle != null && this.options?.gpi) {
      this.options.gpi.destroy(this.gpiHandle);
      this.gpiHandle = null;
      this.mountedHostEl = null;
    }
    super.destroy(...args);
  }

  private mountGpiHost(): void {
    const gpi = this.options?.gpi;
    const hostEl = this.refs?.[GPI_HOST_REF];
    if (!gpi || !hostEl) return;
    // A stale handle means Form.io replaced our subtree (host detached) — tear it down first.
    if (this.gpiHandle != null) {
      gpi.destroy(this.gpiHandle);
      this.gpiHandle = null;
    }
    this.gpiHandle = gpi.mount(hostEl);
    this.mountedHostEl = hostEl;
  }
}

let registered = false;

/**
 * Register the `gpipayment` component globally with Form.io (idempotent). Call once per app
 * bootstrap that renders forms — EMR, patient portal, form sandbox. `translate` (optional) is
 * captured so the static placeholder copy is localized; the last non-null one wins.
 */
export function registerGpiPaymentComponent(translate?: (key: string) => string): void {
  if (translate) translateFn = translate;
  if (registered) return;
  Components.addComponent(GPI_PAYMENT_TYPE, GpiPaymentComponent);
  registered = true;
}
